import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowLeft, Copy, MoreVertical, Paperclip, Send, Trash2, X } from 'lucide-react';
import { chatApi } from '@/entities/message';
import { matchApi } from '@/entities/match';
import { useActiveEvent } from '@/entities/event';
import { UserAvatar } from '@/entities/user';
import { AttachmentMenu, type AttachmentSource } from '@/features/chat/ui/AttachmentMenu';
import { AttachmentPreview, type PendingAttachment } from '@/features/chat/ui/AttachmentPreview';
import { ConfirmDialog } from '@/features/chat/ui/ConfirmDialog';
import { useDeleteMessage } from '@/features/chat/hooks/useDeleteMessage';
import { useAuthStore } from '@/shared/stores/authStore';
import { QUERY_KEYS, ROUTES } from '@/shared/config/constants';
import { Button, MediaViewer, MessageBubble, Skeleton } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { getActiveSocket } from '@/shared/lib/socketLifecycle';
import type { Message } from '@/entities/message';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB
const DELETE_WINDOW_MS = 7 * 60 * 1000; // 7 min, alinhado com backend
const detectAttachmentType = (file: File): PendingAttachment['type'] => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  return 'other';
};

const QUICK_REPLIES = [
  'Vamos conversar?',
  'Posso te ajudar com isso',
  'Qual seu melhor horário?',
  'Te envio um material',
];

export const ChatConversationPage = () => {
  const { matchId = '' } = useParams<{ matchId: string }>();
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [draft, setDraft] = useState('');
  const [viewerMedia, setViewerMedia] = useState<
    { url: string; name: string | null; type: string | null } | null
  >(null);
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<{ ids: string[] } | null>(null);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deleteMessageMutation = useDeleteMessage(matchId);

  const { eventoId } = useActiveEvent();
  const { data: matchesData } = useQuery({
    queryKey: [...QUERY_KEYS.MATCHES, eventoId] as const,
    queryFn: () => matchApi.list(eventoId ?? ''),
    enabled: !!eventoId,
    staleTime: 5 * 60 * 1000,
  });
  const match = matchesData?.matches.find((m) => m.id === matchId);

  const { data: msgs, isLoading } = useQuery({
    queryKey: QUERY_KEYS.MESSAGES(matchId),
    queryFn: () => chatApi.listMessages(matchId),
    enabled: !!matchId,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    if (!matchId) return;
    chatApi.markRead(matchId).catch(() => undefined);
  }, [matchId, msgs?.messages.length]);

  const sendMutation = useMutation({
    mutationFn: (content: string) => chatApi.sendMessage(matchId, content),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(matchId) });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    },
  });

  // Envia a quick reply diretamente sem passar pelo input -- comportamento
  // "send instant" de chat moderno. Cuidado para nao perder o texto que o
  // usuario ja estava digitando: se ha draft, ocultamos as chips.
  const sendQuickReply = (text: string) => {
    if (sendMutation.isPending) return;
    sendMutation.mutate(text);
  };

  const uploadFileMutation = useMutation({
    mutationFn: async ({ file, caption }: { file: File; caption: string }) => {
      const uploaded = await chatApi.uploadFile(file);
      return chatApi.sendFileMessage(
        matchId,
        {
          fileUrl: uploaded.fileUrl,
          fileName: uploaded.fileName,
          fileType: uploaded.fileType,
        },
        caption || undefined,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(matchId) });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
      toast.success('Arquivo enviado');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Falha ao enviar arquivo');
    },
  });

  // Substitui o envio imediato por preview pre-confirmacao (estilo
  // WhatsApp). Apenas cria URL local; upload acontece em handleSendAttachment.
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reselecionar mesmo arquivo
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error('Arquivo muito grande. Limite de 10 MB.');
      return;
    }
    setPendingAttachment({
      id: `${file.name}-${file.lastModified}-${file.size}`,
      file,
      previewUrl: URL.createObjectURL(file),
      type: detectAttachmentType(file),
    });
  };

  const handleCancelAttachment = useCallback(() => {
    setPendingAttachment((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, []);

  const handleSendAttachment = useCallback(
    async (caption: string) => {
      if (!pendingAttachment) return;
      await uploadFileMutation.mutateAsync({ file: pendingAttachment.file, caption });
      URL.revokeObjectURL(pendingAttachment.previewUrl);
      setPendingAttachment(null);
    },
    [pendingAttachment, uploadFileMutation],
  );

  // Cleanup defensivo: se a pagina desmontar com preview aberto (ex.: usuario
  // navega de volta), revogamos a object URL para evitar leak de memoria.
  useEffect(() => {
    return () => {
      if (pendingAttachment) URL.revokeObjectURL(pendingAttachment.previewUrl);
    };
  }, [pendingAttachment]);

  // ===== Selection mode (long-press estilo WhatsApp) =====
  const isSelectionMode = selectedIds.size > 0;

  const handleLongPress = useCallback((messageId: string) => {
    setSelectedIds((prev) => {
      if (prev.has(messageId)) return prev;
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
  }, []);

  const handleSelectToggle = useCallback((messageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Sai do modo selecao com Esc.
  useEffect(() => {
    if (!isSelectionMode) return;
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') exitSelectionMode();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isSelectionMode, exitSelectionMode]);

  // Sai do modo selecao se trocar de conversa.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [matchId]);

  // ===== Acoes em lote sobre a selecao =====
  const allMessages = msgs?.messages ?? [];
  const selectedMessages = useMemo(
    () => allMessages.filter((m) => selectedIds.has(m.id)),
    [allMessages, selectedIds],
  );

  // Regras de habilitacao do botao Apagar (alinhadas com o backend):
  //  - todas precisam ser do proprio user
  //  - todas dentro da janela de 7 minutos
  //  - nenhuma ja deletada
  const canDeleteSelection = useMemo(() => {
    if (selectedMessages.length === 0 || !userId) return false;
    const now = Date.now();
    return selectedMessages.every(
      (m) =>
        m.authorId === userId &&
        !m.isDeleted &&
        now - new Date(m.createdAt).getTime() <= DELETE_WINDOW_MS,
    );
  }, [selectedMessages, userId]);

  const canCopySelection = useMemo(
    () => selectedMessages.some((m) => !m.isDeleted && m.content.trim().length > 0),
    [selectedMessages],
  );

  const handleCopySelected = useCallback(async () => {
    const texts = [...selectedMessages]
      .filter((m) => !m.isDeleted && m.content.trim().length > 0)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((m) => m.content)
      .join('\n');
    if (!texts) return;
    try {
      await navigator.clipboard.writeText(texts);
      toast.success(
        selectedMessages.length === 1
          ? 'Mensagem copiada'
          : `${selectedMessages.length} mensagens copiadas`,
      );
      exitSelectionMode();
    } catch {
      toast.error('Nao foi possivel copiar.');
    }
  }, [exitSelectionMode, selectedMessages]);

  const requestDeleteSelected = useCallback(() => {
    if (!canDeleteSelection) return;
    setConfirmDelete({ ids: selectedMessages.map((m) => m.id) });
  }, [canDeleteSelection, selectedMessages]);

  const performDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setBatchDeleting(true);
    try {
      // Sequencial em vez de paralelo: respeitamos rate limit do backend e
      // evitamos race entre os optimistic updates (cada um le e reescreve
      // a mesma cache key).
      for (const id of confirmDelete.ids) {
        await deleteMessageMutation.mutateAsync(id).catch(() => undefined);
      }
    } finally {
      setBatchDeleting(false);
      setConfirmDelete(null);
      exitSelectionMode();
    }
  }, [confirmDelete, deleteMessageMutation, exitSelectionMode]);

  // ===== Attachment menu (bottom sheet com 3 fontes) =====
  const handleAttachmentSourceSelect = useCallback(
    (source: AttachmentSource) => {
      setAttachmentMenuOpen(false);
      const input = fileInputRef.current;
      if (!input) return;
      // Reset attrs e configura para a fonte escolhida. Mantemos UM input
      // hidden no JSX para preservar o ref/onChange existentes (que
      // alimentam o pending preview flow).
      input.removeAttribute('capture');
      if (source === 'camera') {
        input.accept = 'image/*';
        input.setAttribute('capture', 'environment');
      } else if (source === 'gallery') {
        input.accept = 'image/*';
      } else {
        input.accept = 'application/pdf,.pdf';
      }
      input.click();
    },
    [],
  );

  // Smart auto-scroll:
  //  - mount inicial: ja posicionado no fim (instant) para mostrar ultima msg
  //  - nova msg + ja perto do fim: scroll smooth ate o fim
  //  - nova msg + scrollado pra cima lendo historico: nao mexe na posicao,
  //    incrementa contador de "novas" e mostra badge clicavel
  const [unreadNew, setUnreadNew] = useState(0);
  const initialScrolledRef = useRef(false);
  const previousCountRef = useRef(0);

  const isNearBottom = (): boolean => {
    const el = scrollRef.current;
    if (!el) return true;
    const threshold = 120; // px de tolerancia
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setUnreadNew(0);
  }, []);

  useEffect(() => {
    const count = msgs?.messages.length ?? 0;
    if (count === 0) return;

    // Primeira renderizacao com mensagens: posicionar no fim sem animacao.
    if (!initialScrolledRef.current) {
      initialScrolledRef.current = true;
      previousCountRef.current = count;
      requestAnimationFrame(() => scrollToBottom('instant'));
      return;
    }

    // Mensagens novas chegaram.
    if (count > previousCountRef.current) {
      const delta = count - previousCountRef.current;
      previousCountRef.current = count;
      if (isNearBottom()) {
        scrollToBottom('smooth');
      } else {
        setUnreadNew((n) => n + delta);
      }
    } else {
      previousCountRef.current = count;
    }
  }, [msgs?.messages.length, scrollToBottom]);

  const handleScroll = () => {
    if (isNearBottom()) setUnreadNew(0);
  };

  useEffect(() => {
    const socket = getActiveSocket();
    if (!socket || !matchId) return;

    type IncomingMessage = {
      id: string;
      matchId: string;
      senderId: string;
      content: string | null;
      tipo: string;
      status: string;
      createdAt: string;
      fileUrl?: string | null;
      fileName?: string | null;
      fileType?: string | null;
      isDeleted?: boolean;
      deletedAt?: string | null;
    };

    const onMessage = (raw: IncomingMessage) => {
      if (raw.matchId !== matchId) return;
      // Importante: propagar fileUrl/fileName/fileType para que imagens
      // enviadas em realtime sejam renderizadas (sem isso, MessageBubble
      // recebe undefined e a imagem so aparece apos reload).
      const next: Message = {
        id: raw.id,
        matchId: raw.matchId,
        authorId: raw.senderId,
        type: (raw.tipo as Message['type']) ?? 'TEXT',
        content: raw.content ?? '',
        read: raw.status === 'LIDO',
        createdAt: raw.createdAt,
        fileUrl: raw.fileUrl ?? null,
        fileName: raw.fileName ?? null,
        fileType: raw.fileType ?? null,
        isDeleted: raw.isDeleted ?? false,
        deletedAt: raw.deletedAt ?? null,
      };
      qc.setQueryData<{ messages: Message[] } | undefined>(
        QUERY_KEYS.MESSAGES(matchId),
        (prev) => {
          const list = prev?.messages ?? [];
          if (list.some((m) => m.id === next.id)) return prev;
          return { messages: [...list, next] };
        },
      );
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    };

    // Soft delete WhatsApp-like: backend emite no room match:<matchId> com
    // { messageId, matchId, deletedAt }. Atualiza o cache marcando a
    // mensagem como deletada e zerando o conteudo (MessageBubble renderiza
    // o placeholder com base em isDeleted).
    const onMessageDeleted = (raw: {
      messageId: string;
      matchId: string;
      deletedAt: string | null;
    }) => {
      if (raw.matchId !== matchId) return;
      qc.setQueryData<{ messages: Message[] } | undefined>(
        QUERY_KEYS.MESSAGES(matchId),
        (prev) => {
          if (!prev) return prev;
          return {
            messages: prev.messages.map((m) =>
              m.id === raw.messageId
                ? {
                    ...m,
                    isDeleted: true,
                    deletedAt: raw.deletedAt ?? new Date().toISOString(),
                    content: '',
                    fileUrl: null,
                    fileName: null,
                    fileType: null,
                  }
                : m,
            ),
          };
        },
      );
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    };

    socket.on('message:new', onMessage);
    socket.on('message:deleted', onMessageDeleted);
    // O backend so encaminha broadcast para sockets que entraram no room do
    // matchId. Sem este 'join:match', o cliente conecta no namespace /chat
    // mas nao recebe message:new de outros participantes.
    socket.emit('join:match', { matchId });

    return () => {
      socket.emit('leave:match', { matchId });
      socket.off('message:new', onMessage);
      socket.off('message:deleted', onMessageDeleted);
    };
  }, [matchId, qc]);

  // Backend retorna mensagens em ordem DESC (cursor-based pagination usa
  // createdAt < cursor). Para renderizar em ordem cronologica (antiga em
  // cima, nova embaixo) revertemos no client. Imutabilidade: nao mutamos
  // o array original do React Query.
  const messages = useMemo(() => {
    const list = msgs?.messages ?? [];
    return [...list].reverse();
  }, [msgs]);

  const handleSend = () => {
    const content = draft.trim();
    if (!content) return;
    sendMutation.mutate(content);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <section className="relative -mx-4 -my-6 flex h-[calc(100dvh-3.5rem-4rem)] flex-col bg-[#F9FAFB] md:-mx-6 lg:-mx-8 lg:-my-8 lg:h-[calc(100dvh-3.5rem)]">
      {isSelectionMode ? (
        <header
          className="flex items-center gap-2 border-b border-gray-100 bg-white px-2 py-3"
          aria-live="polite"
        >
          <button
            type="button"
            onClick={exitSelectionMode}
            aria-label="Cancelar seleção"
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
          <span className="flex-1 text-sm font-semibold tracking-tight text-gray-900">
            {selectedIds.size} selecionada{selectedIds.size > 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={handleCopySelected}
            disabled={!canCopySelection}
            aria-label="Copiar mensagens selecionadas"
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Copy className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={requestDeleteSelected}
            disabled={!canDeleteSelection}
            aria-label="Apagar mensagens selecionadas"
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
              canDeleteSelection ? 'text-red-600' : 'text-gray-400',
            )}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </header>
      ) : (
        <header className="flex items-center gap-3 border-b border-gray-100 bg-white px-3 py-3">
          <Button asChild variant="ghost" size="icon" aria-label="Voltar">
            <Link to={ROUTES.APP.CHAT}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          {match ? (
            <>
              <UserAvatar
                nome={match.participante.nome}
                fotoUrl={match.participante.fotoUrl ?? null}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold tracking-tight text-gray-900">
                  {match.participante.nome}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {match.participante.cargo}
                  {match.participante.empresa ? ` · ${match.participante.empresa}` : ''}
                </p>
              </div>
            </>
          ) : (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">Conversa</p>
            </div>
          )}
          <button
            type="button"
            aria-label="Mais opções"
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
        </header>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative flex-1 overflow-y-auto px-4 py-4"
      >
        {isLoading ? (
          <div
            className="mx-auto flex max-w-2xl flex-col gap-2"
            aria-busy="true"
            aria-label="Carregando mensagens"
          >
            {[
              { own: false, w: 'w-44' },
              { own: true, w: 'w-56' },
              { own: false, w: 'w-64' },
              { own: false, w: 'w-32' },
              { own: true, w: 'w-48' },
              { own: true, w: 'w-72' },
            ].map((b, i) => (
              <div key={i} className={`flex w-full ${b.own ? 'justify-end' : 'justify-start'}`}>
                <Skeleton
                  className={`h-9 ${b.w} ${
                    b.own ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'
                  }`}
                />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-muted-foreground">Diga olá e quebre o gelo</p>
          </div>
        ) : (
          <div
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            className="mx-auto flex max-w-2xl flex-col"
          >
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const sameAuthorAsPrev = prev && prev.authorId === m.authorId;
              // Mesmo autor consecutivo: gap apertado (4px) agrupa. Mudanca
              // de autor: gap maior (12px) da respiro visual.
              const spacing = sameAuthorAsPrev ? 'mt-1' : i === 0 ? '' : 'mt-3';
              return (
                <div key={m.id} className={spacing}>
                  <MessageBubble
                    message={m}
                    isOwn={m.authorId === userId}
                    onOpenMedia={setViewerMedia}
                    selectionMode={isSelectionMode}
                    isSelected={selectedIds.has(m.id)}
                    onLongPress={handleLongPress}
                    onSelectToggle={handleSelectToggle}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {unreadNew > 0 ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[140px] z-10 flex justify-center px-4">
          <button
            type="button"
            onClick={() => scrollToBottom('smooth')}
            className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3.5 py-2 text-xs font-semibold text-white shadow-lg ring-1 ring-black/10 transition-transform hover:scale-105"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {unreadNew} nova{unreadNew > 1 ? 's' : ''}
          </button>
        </div>
      ) : null}

      <div className="border-t border-gray-100 bg-white px-3 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+10px)]">
        <div className="mx-auto max-w-2xl">
          {/* Quick replies: enviam direto (estilo "send instant"). Escondidas
              quando o usuario ja esta escrevendo para nao interferir. */}
          {!draft.trim() ? (
            <div className="mb-2 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => sendQuickReply(q)}
                  disabled={sendMutation.isPending}
                  className="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              aria-label="Anexar arquivo"
              onClick={() => setAttachmentMenuOpen(true)}
              disabled={uploadFileMutation.isPending}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-50"
            >
              <Paperclip className="h-5 w-5" />
            </button>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Escreva uma mensagem..."
              className="max-h-32 flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            {/* Botao enviar estilo iMessage: preto quando tem texto, cinza
                claro quando vazio. */}
            <button
              type="button"
              onClick={handleSend}
              disabled={!draft.trim() || sendMutation.isPending}
              aria-label="Enviar mensagem"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition-all disabled:bg-gray-200 disabled:text-gray-400"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <MediaViewer
        open={!!viewerMedia}
        url={viewerMedia?.url ?? ''}
        name={viewerMedia?.name ?? null}
        type={viewerMedia?.type ?? null}
        onClose={() => setViewerMedia(null)}
      />

      <AttachmentMenu
        open={attachmentMenuOpen}
        onClose={() => setAttachmentMenuOpen(false)}
        onSelect={handleAttachmentSourceSelect}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title={
          confirmDelete && confirmDelete.ids.length > 1
            ? `Apagar ${confirmDelete.ids.length} mensagens?`
            : 'Apagar mensagem?'
        }
        description="Esta ação não pode ser desfeita. A mensagem será apagada para todos."
        confirmText="Apagar"
        destructive
        pending={batchDeleting}
        onConfirm={performDelete}
        onCancel={() => (batchDeleting ? undefined : setConfirmDelete(null))}
      />

      {pendingAttachment ? (
        <AttachmentPreview
          attachment={pendingAttachment}
          onSend={handleSendAttachment}
          onCancel={handleCancelAttachment}
        />
      ) : null}
    </section>
  );
};
